import { NgZone } from '@angular/core';
import {
  DocumentData,
  DocumentReference,
  Query,
  onSnapshot,
} from 'firebase/firestore';
import {
  Observable,
  ReplaySubject,
  retry,
  share,
  throwError,
  timer,
} from 'rxjs';
import { observeInZone } from './ZoneUtils';

/**
 * Firestore error codes that mean "not yet", as opposed to "no". A connection that has
 * not come up, a backend blip, a token that lands a moment after the listen.
 */
const TEMPORARY = new Set([
  'unavailable',
  'deadline-exceeded',
  'internal',
  'cancelled',
  'aborted',
  'resource-exhausted',
  'unknown',
  'unauthenticated',
]);

/**
 * A live listen is the screen's lifeline: when it errors, the view it feeds has nothing
 * to fall back on and keeps showing its spinner until someone reloads the page. This sits
 * at the same seam as the zone re-entry below, and for the same reason: applied per call
 * site, two of the six listeners had it and four did not, so a backend blip during a cold
 * start left /overzicht and /beheer blank until the member hit refresh.
 *
 * Temporary failures retry with backoff for as long as the screen is open. Anything else
 * is a real answer and surfaces after one retry, which covers a token arriving late.
 */
const keepListening = <T>() =>
  retry<T>({
    delay: (error: unknown, attempt: number) => {
      const code = String((error as { code?: string })?.code ?? '').replace(
        /^firestore\//,
        '',
      );
      if (!TEMPORARY.has(code) && attempt > 1) {
        return throwError(() => error);
      }
      // 1s, 2s, 4s, capped at half a minute: a phone that spends a minute in a tunnel
      // still comes back on its own, without hammering the backend on the way.
      return timer(Math.min(1000 * 2 ** (attempt - 1), 30_000));
    },
  });

/** The retry policy on its own, for the spec: the factories below need a real Firestore
 *  query, which cannot be faked, and this is the part with the behaviour. */
export const keepListeningForTest = keepListening;

/**
 * RxJS over Firestore's callback listeners, replacing @angular/fire's collectionData /
 * docData.
 *
 * Zone re-entry is applied here rather than left to each caller. Firestore delivers
 * snapshots off its own WebChannel callback, never through Angular, so a stream feeding a
 * template updates state without a change-detection pass unless it re-enters the zone
 * (frozen spinners on slow devices, where a refresh appears to "fix" it). Doing it at this
 * seam means a new listener cannot forget it.
 *
 * Neither observable completes: they mirror a live listener, and unsubscribing detaches it.
 */
export function collectionData$<T>(
  query: Query<DocumentData>,
  zone: NgZone,
): Observable<T[]> {
  return new Observable<T[]>((subscriber) =>
    onSnapshot(
      query,
      (snapshot) => subscriber.next(snapshot.docs.map((d) => d.data() as T)),
      (error) => subscriber.error(error),
    ),
  ).pipe(observeInZone(zone), keepListening());
}

/** Like collectionData$, but keeps the document id. The reservation mirrors are keyed by
 *  user id (classes/{id}/users/{userId}), so the id is data the caller needs. */
export function collectionSnapshots$<T>(
  query: Query<DocumentData>,
  zone: NgZone,
): Observable<{ id: string; data: T }[]> {
  return new Observable<{ id: string; data: T }[]>((subscriber) =>
    onSnapshot(
      query,
      (snapshot) =>
        subscriber.next(
          snapshot.docs.map((d) => ({ id: d.id, data: d.data() as T })),
        ),
      (error) => subscriber.error(error),
    ),
  ).pipe(observeInZone(zone), keepListening());
}

export function docData$<T>(
  ref: DocumentReference<DocumentData>,
  zone: NgZone,
): Observable<T | undefined> {
  return new Observable<T | undefined>((subscriber) =>
    onSnapshot(
      ref,
      (snapshot) => subscriber.next(snapshot.data() as T | undefined),
      (error) => subscriber.error(error),
    ),
  ).pipe(observeInZone(zone), keepListening());
}

// share() with these options keeps the shareReplay semantics the views rely on (lazy
// connect, replay the latest snapshot to late subscribers, disconnect when the last
// unsubscribes) with one crucial difference: an error RESETS the share instead of being
// cached forever. shareReplay({refCount: true}) never resets on error (resetOnError is
// hardwired false), so one failed listen froze /reserveer for the whole SPA session.
export const resettableReplay = <T>() =>
  share<T>({
    connector: () => new ReplaySubject<T>(1),
    resetOnError: true,
    resetOnComplete: true,
    resetOnRefCountZero: true,
  });
